import { __rest } from "tslib";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useRef, forwardRef } from 'react';
import classNames from 'classnames';
import noop from 'lodash/fp/noop';
import useAfterMount from '../../hooks/useAfterMount';
const DEFAULT_DIGIT_PRECISION = 3;
export const DEFAULT_VALUE = 0;
export const DEFAULT_STEP = 1;
export const DEFAULT_MIN = 0;
export const DEFAULT_MAX = Number.MAX_VALUE;
const validKeys = [
    '0',
    '1',
    '2',
    '3',
    '4',
    '5',
    '6',
    '7',
    '8',
    '9',
    '.',
    ',',
    '-',
    'Backspace',
    'Tab',
    'ArrowLeft',
    'ArrowRight',
    'ArrowDown',
    'ArrowUp',
];
// Note: even if limits are set and input type is number, many browsers allow to enter invalid data
// like entering characters or values outside the boundaries, hence we have to check the input here
// See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/number
const getValueFromProps = (val, min, max, placeholder, noDefault) => {
    // Show placeholder or nothing if given instead of 0 if requested for
    if (val === undefined && (placeholder || noDefault)) {
        return '';
    }
    return clampNumber(Number(val), min, max) || DEFAULT_VALUE;
};
const clampNumber = (value, min, max) => {
    if (value < min) {
        return min;
    }
    if (value > max) {
        return max;
    }
    return value;
};
export const convertNonIntegerToDefault = (value, fallback) => {
    return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
};
const isMultipleKeyInput = (key, event, previousKeyRef) => {
    return event.key === key && previousKeyRef.current.includes(key);
};
const isInvalidAfter = (key, possiblePreviousKeys, event, previousKeyRef) => {
    return event.key === key && possiblePreviousKeys.includes(previousKeyRef.current);
};
const NumberInput = forwardRef((props, ref) => {
    const { id, min: propMin, max: propMax, value: propValue, step = DEFAULT_STEP, disabled = false, noDefault = false, onChange, onValueChanged = noop, onKeyDown = noop, bsSize = 'md', unit, inputAddon, errorMessage, warningMessage, messageWhiteSpace = 'normal', controls, placeholder, digitPrecision = DEFAULT_DIGIT_PRECISION, className = '' } = props, remainingProps = __rest(props, ["id", "min", "max", "value", "step", "disabled", "noDefault", "onChange", "onValueChanged", "onKeyDown", "bsSize", "unit", "inputAddon", "errorMessage", "warningMessage", "messageWhiteSpace", "controls", "placeholder", "digitPrecision", "className"]);
    const previousKeyRef = useRef('');
    const callback = onChange || onValueChanged;
    const min = convertNonIntegerToDefault(propMin, DEFAULT_MIN);
    const max = convertNonIntegerToDefault(propMax, DEFAULT_MAX);
    const value = getValueFromProps(propValue, min, max, placeholder, noDefault);
    // Define local state and define initial values
    const [state, setState] = useState({
        value,
        enteredValue: value,
        isValid: true,
    });
    // Update internal value whenever the value prop from outside changes
    useAfterMount(() => {
        setState({
            value: getValueFromProps(propValue, min, max, placeholder, noDefault),
            enteredValue: value,
            isValid: true,
        });
    }, [propValue, min, max, placeholder, noDefault, value]);
    const applyValue = (newValue) => {
        if (newValue === '-' || newValue === '') {
            setState({
                value: newValue,
                enteredValue: newValue,
                isValid: true,
            });
            return;
        }
        const enteredValue = Number(newValue);
        const isValid = !isNaN(enteredValue) && enteredValue >= min && enteredValue <= max;
        const newValidValue = clampNumber(enteredValue, min, max);
        setState({
            value: newValidValue,
            enteredValue,
            isValid,
        });
        // Only call back the caller for valid values
        isValid && callback(newValidValue);
    };
    const handleOnChange = (event) => {
        applyValue(event.target.value);
    };
    // Prevent entering exponent to avoid side effects
    // See https://bugzilla.mozilla.org/show_bug.cgi?id=1398528
    // Also preventing letter inputs or multiple consecutive characters
    // that is possible by Firefox and Safari
    const handleKeyDown = (event) => {
        // Allow for key combination like "ctrl + a"
        if (event.ctrlKey || event.altKey || event.metaKey) {
            return;
        }
        // Check for multiple dots, commas or dashes (consecutive)
        if (isMultipleKeyInput('.', event, previousKeyRef) ||
            isMultipleKeyInput(',', event, previousKeyRef) ||
            isMultipleKeyInput('-', event, previousKeyRef) ||
            isInvalidAfter(',', ['.', '-'], event, previousKeyRef) ||
            isInvalidAfter('.', [',', '-'], event, previousKeyRef) ||
            isInvalidAfter('-', ['.', ','], event, previousKeyRef) ||
            event.key === 'Process' ||
            event.key === 'Dead') {
            event.preventDefault();
        }
        // Filter out everything that is not a number
        if (!validKeys.includes(event.key)) {
            event.preventDefault();
        }
        previousKeyRef.current = event.key;
        onKeyDown(event);
    };
    const handleBlur = () => {
        // When the value is removed, keep the input empty but trigger the outside callback
        // since the user has finished his input
        const lastEnteredValue = state.enteredValue;
        if (lastEnteredValue === '') {
            callback();
            return;
        }
        // If there is no value defined and a placeholder is given or the default is not wanted,
        // keep the input empty
        if (!state.value && (noDefault || placeholder)) {
            return;
        }
        // Otherwise, validate the input, round it according to digitPrecision,
        // and clamp the value if the entered value exceeds the limitations
        const convertedEnteredValue = convertNonIntegerToDefault(Number(lastEnteredValue), DEFAULT_VALUE);
        const validNumber = clampNumber(Number(convertedEnteredValue.toFixed(digitPrecision)), min, max);
        applyValue(validNumber);
    };
    const handleWheel = (event) => {
        const target = event.target;
        if (target instanceof HTMLInputElement) {
            target.blur();
        }
    };
    const inputGroupClassNames = classNames('input-group', bsSize === 'sm' && 'input-group-sm', bsSize === 'lg' && 'input-group-lg');
    const inputClassNames = classNames('form-control', 'no-controls', bsSize === 'sm' && 'input-sm', bsSize === 'lg' && 'input-lg', className);
    const hasFeedback = errorMessage || warningMessage;
    const input = (_jsx("input", Object.assign({}, remainingProps, { id: id, type: 'number', step: step, min: min, max: max, value: state.isValid ? state.value : state.enteredValue, className: inputClassNames, disabled: disabled, onBlur: handleBlur, 
        // onChange={state.value ? handleOnChange : undefined}
        onChange: handleOnChange, onKeyDown: handleKeyDown, ref: ref, "aria-label": 'number-input', placeholder: placeholder, onWheel: handleWheel })));
    return (_jsxs("div", Object.assign({ className: inputGroupClassNames }, { children: [inputAddon && (_jsx("div", Object.assign({ className: 'input-group-addon' }, { children: _jsx("span", { className: inputAddon }) }))), _jsxs("div", Object.assign({ className: 'form-control-feedback-wrapper' }, { children: [input, hasFeedback && (_jsxs(_Fragment, { children: [errorMessage && _jsx("span", { className: 'form-control-feedback rioglyph rioglyph-error-sign' }), warningMessage && _jsx("span", { className: 'form-control-feedback rioglyph rioglyph-warning-sign' }), _jsx("span", Object.assign({ className: `help-block white-space-${messageWhiteSpace}` }, { children: _jsx("span", { children: errorMessage || warningMessage }) }))] }))] })), (unit || controls) && (_jsxs("div", Object.assign({ className: `input-group-addon ${disabled ? 'disabled pointer-events-none' : ''}` }, { children: [unit && unit, controls && controls] })))] })));
});
export default NumberInput;
